import React, { useState, useEffect } from 'react';
import { FaBuilding, FaUsers, FaChartLine, FaPlus, FaSearch, FaFilter, FaUpload, FaTrash, FaExclamationTriangle, FaFileExcel, FaDownload, FaUserShield, FaEnvelope } from 'react-icons/fa';
import OrganizationOverview from './OrganizationOverview';
import { CompleteOrganizationWizard } from './CompleteOrganizationWizard';
import { QuickOrganizationSetup } from './QuickOrganizationSetup';
import RealUserManagementPanel from './RealUserManagementPanel';
import OrganizationDataViewer from './OrganizationDataViewer';
import ImportWizard from './ImportWizard';
import AccessControlPanel from './AccessControlPanel';
import ContactRequestsPanel from './ContactRequestsPanel';
import { organizationService, DashboardStats } from '../../services/organizationService';
import { OrganizationData } from '../../services/databaseService';

interface AdminDashboardProps {
  userRole: string;
  userId: string;
  initialTab?: 'overview' | 'organizations' | 'users' | 'data' | 'access-control' | 'contacts';
}



export const AdminDashboard: React.FC<AdminDashboardProps> = ({ userRole, userId, initialTab = 'overview' }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'organizations' | 'users' | 'data' | 'access-control' | 'contacts'>(initialTab);
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [showCompleteWizard, setShowCompleteWizard] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationData[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalOrganizations: 0,
    activeOrganizations: 0,
    totalUsers: 0,
    totalStudents: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended' | 'trial'>('all');
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationData | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] = useState<OrganizationData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [importType, setImportType] = useState<'students' | 'teachers' | 'organizations'>('students');

  // Check if user has admin privileges
  const isSystemAdmin = userRole === 'admin' || userRole === 'OrgAdmin';

  // Update activeTab when initialTab changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (isSystemAdmin) {
      loadDashboardData();
    }
  }, [isSystemAdmin]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar organizaciones desde MongoDB
      try {
        const orgResult = await organizationService.getOrganizations();
        
        if (orgResult.success) {
          setOrganizations(orgResult.organizations);
        } else {
          console.warn('No se pudieron cargar las organizaciones:', orgResult.message);
          setOrganizations([]);
        }
      } catch (orgError) {
        console.warn('Error cargando organizaciones:', orgError);
        setOrganizations([]);
      }

      // Cargar estadísticas desde MongoDB
      try {
        const statsResult = await organizationService.getDashboardStats();
        
        if (statsResult.success && statsResult.stats) {
          setStats(statsResult.stats);
        } else {
          console.warn('No se pudieron cargar las estadísticas');
          setStats({
            totalOrganizations: 0,
            activeOrganizations: 0,
            totalUsers: 0,
            totalStudents: 0
          });
        }
      } catch (statsError) {
        console.warn('Error cargando estadísticas:', statsError);
        setStats({
          totalOrganizations: 0,
          activeOrganizations: 0,
          totalUsers: 0,
          totalStudents: 0
        });
      }

    } catch (error) {
      console.error('Error general loading dashboard data:', error);
      setError('Error cargando datos del dashboard. Algunos servicios pueden no estar disponibles.');
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationCreated = (newOrg: OrganizationData) => {
    setOrganizations(prev => [...prev, newOrg]);
    setStats(prev => ({
      ...prev,
      totalOrganizations: prev.totalOrganizations + 1,
      activeOrganizations: newOrg.status === 'active' ? prev.activeOrganizations + 1 : prev.activeOrganizations
    }));
    setActiveTab('organizations');
  };

  const handleOrganizationUpdated = (updatedOrg: OrganizationData) => {
    setOrganizations(prev => 
      prev.map(org => org._id === updatedOrg._id ? updatedOrg : org)
    );
    // Refresh stats
    loadDashboardData();
  };

  const handleDeleteOrganization = (org: OrganizationData) => {
    setOrganizationToDelete(org);
    setShowDeleteModal(true);
  };

  const confirmDeleteOrganization = async () => {
    if (!organizationToDelete) return;

    try {
      setDeleteLoading(true);
      
      // Usar organizationService para eliminar desde MongoDB
      const result = await organizationService.deleteOrganization(organizationToDelete.tenantKey);
      
      if (result.success) {
        // Recargar datos después de eliminar
        await loadDashboardData();
        
        setShowDeleteModal(false);
        setOrganizationToDelete(null);
        
        // Mostrar mensaje de éxito
        alert('Organización eliminada exitosamente');
      } else {
        alert('Error al eliminar la organización: ' + result.message);
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      alert('Error al eliminar la organización. Inténtalo de nuevo.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const downloadTemplate = async (type: 'students' | 'teachers' | 'organizations') => {
    try {
      // Dynamically import xlsx to create template
      const XLSX = await import('xlsx');
      
      let headers: string[] = [];
      let sampleData: any[] = [];
      
      switch (type) {
        case 'students':
          headers = ['ID/RUT', 'Nombre Completo', 'Curso', 'Email', 'Fecha Nacimiento', 'Género'];
          sampleData = [
            ['12345678-9', 'Juan Pérez González', '8° Básico A', 'juan.perez@email.com', '2010-05-15', 'M'],
            ['98765432-1', 'María García López', '8° Básico A', 'maria.garcia@email.com', '2010-08-22', 'F']
          ];
          break;
        case 'teachers':
          headers = ['Usuario', 'Email', 'Nombre', 'Apellido', 'Rol', 'Cursos', 'Teléfono'];
          sampleData = [
            ['jperez', 'juan.perez@colegio.cl', 'Juan', 'Pérez', 'teacher', '8° Básico A, 7° Básico B', '+56912345678'],
            ['mgarcia', 'maria.garcia@colegio.cl', 'María', 'García', 'admin', 'Todos', '+56987654321']
          ];
          break;
        case 'organizations':
          headers = ['Nombre', 'Clave', 'Dominio', 'Estado', 'Descripción'];
          sampleData = [
            ['Colegio San José', 'colegio-san-jose', 'sanjose.edu.cl', 'active', 'Colegio de educación básica y media'],
            ['Instituto Técnico', 'instituto-tecnico', 'tecnico.edu.cl', 'trial', 'Instituto de educación técnica']
          ];
          break;
      }

      // Create workbook
      const wb = XLSX.utils.book_new();
      const wsData = [headers, ...sampleData];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Style headers
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "EEEEEE" } }
        };
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
      
      // Download file
      const fileName = `plantilla_${type}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Error al generar la plantilla. Inténtalo de nuevo.');
    }
  };

  const filteredOrganizations = organizations.filter(org => {
    const matchesSearch = org.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         org.tenantKey.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || org.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (!isSystemAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">🚫</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Acceso Denegado
          </h2>
          <p className="text-gray-600">
            No tienes permisos para acceder al panel administrativo.
          </p>
        </div>
      </div>
    );
  }

  // Mostrar loading solo durante la carga inicial crítica
  if (loading && organizations.length === 0 && !error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando panel administrativo...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: FaChartLine },
    { id: 'organizations', label: 'Organizaciones', icon: FaBuilding },
    { id: 'users', label: 'Gestión de Usuarios', icon: FaUsers },
    { id: 'contacts', label: 'Solicitudes Contacto', icon: FaEnvelope },
    { id: 'access-control', label: 'Control de Acceso', icon: FaUserShield },
    { id: 'data', label: 'Datos e Importación', icon: FaSearch }
  ];

  return (
    <div className="bg-gray-50">
      {/* Error notification (non-blocking) */}
      {error && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                {error}
              </p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => setError(null)}
                  className="inline-flex bg-yellow-50 rounded-md p-1.5 text-yellow-500 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-yellow-50 focus:ring-yellow-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div>
        {activeTab === 'overview' && (
          <OrganizationOverview 
            stats={stats}
            organizations={organizations}
            onRefresh={loadDashboardData}
          />
        )}

        {activeTab === 'organizations' && (
          <div className="space-y-6">
            {/* Header con botón de crear */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Gestión de Organizaciones</h3>
                  <p className="text-sm text-gray-600">Administra todas las organizaciones del sistema</p>
                </div>
                <button
                  onClick={() => setShowCompleteWizard(true)}
                  className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <FaPlus className="w-4 h-4" />
                  <span>Nueva Organización</span>
                </button>
              </div>
              
              {/* Filters */}
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Buscar organizaciones..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <FaFilter className="text-gray-400 w-4 h-4" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="active">Activas</option>
                    <option value="trial">En prueba</option>
                    <option value="suspended">Suspendidas</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Organizations List */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredOrganizations.map((org) => (
                <div key={org._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{org.displayName}</h3>
                      <p className="text-sm text-gray-600">{org.tenantKey}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      org.status === 'active' ? 'bg-green-100 text-green-800' :
                      org.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {org.status === 'active' ? 'Activa' : 
                       org.status === 'trial' ? 'Prueba' : 'Suspendida'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div>Dominios: {org.domains.join(', ')}</div>
                    <div>Usuarios: {org.userCount || 0}</div>
                    <div>Estudiantes: {org.studentCount || 0}</div>
                    <div>Creada: {new Date(org.createdAt).toLocaleDateString()}</div>
                  </div>

                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => {
                        setActiveTab('users');
                        // TODO: Set selected organization for user management
                      }}
                      className="flex-1 bg-gray-500 hover:bg-gray-600 text-white text-sm px-3 py-2 rounded-lg transition-colors"
                    >
                      Usuarios
                    </button>
                    <button
                      onClick={() => handleDeleteOrganization(org)}
                      className="bg-red-500 hover:bg-red-600 text-white text-sm px-3 py-2 rounded-lg transition-colors"
                      title="Eliminar organización"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredOrganizations.length === 0 && (
              <div className="text-center py-12">
                <FaBuilding className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No se encontraron organizaciones
                </h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Intenta ajustar los filtros de búsqueda'
                    : 'No hay organizaciones disponibles'
                  }
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <button
                    onClick={() => setShowCompleteWizard(true)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Crear Nueva Organización
                  </button>
                )}
              </div>
            )}
          </div>
        )}



        {activeTab === 'users' && (
          <RealUserManagementPanel 
            onUserUpdated={() => loadDashboardData()}
          />
        )}

        {activeTab === 'access-control' && (
          <AccessControlPanel />
        )}

        {activeTab === 'contacts' && (
          <ContactRequestsPanel />
        )}

        {activeTab === 'data' && (
          <div className="space-y-6">
            {/* Sección de Visualización de Datos por Organización */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Visualizar Datos por Organización</h3>
                  <p className="text-gray-600">
                    Selecciona una organización para ver sus datos detallados
                  </p>
                </div>
                <button 
                  onClick={() => setShowImportWizard(true)}
                  className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <FaUpload className="w-4 h-4" />
                  <span>Importar Datos</span>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {organizations.map((org) => (
                  <button
                    key={org._id}
                    onClick={() => setSelectedOrganization(org)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                  >
                    <div className="font-medium text-gray-900">{org.displayName}</div>
                    <div className="text-sm text-gray-600">{org.tenantKey}</div>
                    <div className="text-xs text-gray-500 mt-2">
                      {org.userCount} usuarios • {org.studentCount} estudiantes
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {selectedOrganization && (
              <OrganizationDataViewer organization={selectedOrganization} />
            )}
          </div>
        )}
      </div>

      {/* Modales */}
      {showQuickSetup && (
        <QuickOrganizationSetup
          onOrganizationCreated={handleOrganizationCreated}
          onCancel={() => setShowQuickSetup(false)}
        />
      )}

      {showCompleteWizard && (
        <CompleteOrganizationWizard
          onOrganizationCreated={handleOrganizationCreated}
          onCancel={() => setShowCompleteWizard(false)}
        />
      )}

      {showImportWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <ImportWizard
              organizationId={selectedOrganization?._id || 'default-org-id'}
              onComplete={(result) => {
                console.log('Import completed:', result);
                loadDashboardData(); // Refresh data
                setShowImportWizard(false);
                setSelectedOrganization(null);
                alert(`Importación completada: ${result.imported} registros importados`);
              }}
              onCancel={() => {
                setShowImportWizard(false);
                setSelectedOrganization(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && organizationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <FaExclamationTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Eliminar Organización
                  </h3>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  ¿Estás seguro de que deseas eliminar la organización{' '}
                  <span className="font-semibold text-gray-900">
                    "{organizationToDelete.displayName}"
                  </span>?
                </p>
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>⚠️ Esta acción no se puede deshacer.</strong>
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    Se eliminarán todos los datos asociados:
                  </p>
                  <ul className="text-sm text-red-700 mt-1 ml-4 list-disc">
                    <li>Todos los usuarios y profesores</li>
                    <li>Todos los estudiantes y cursos</li>
                    <li>Todos los informes y evaluaciones</li>
                    <li>Configuraciones y branding</li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setOrganizationToDelete(null);
                  }}
                  disabled={deleteLoading}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDeleteOrganization}
                  disabled={deleteLoading}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {deleteLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Eliminando...</span>
                    </>
                  ) : (
                    <>
                      <FaTrash className="w-4 h-4" />
                      <span>Eliminar Definitivamente</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;